import { useMemo } from "react"
import clsx from "clsx"
import { Feature } from "geojson"

import {
  Typography,
  CardMedia,
  CardContent,
  Stack,
  Box,
  useTheme,
  Card,
  SxProps,
} from "@mui/material"
import { deepmerge } from "@mui/utils"

import {
  getFeatureDetail,
  IEventDetail,
  IPromotionDetail,
  StatusByDateRange,
} from "utils/feature"
import { getDurationDate } from "utils/date"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { LocationIcon } from "components/icons"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { HighlightChip } from "components/chips/HighlightChip"
import { ComingSoonChip } from "components/chips"

export interface IVerticalCardProps {
  data: Feature
  dense?: boolean
  sx?: SxProps
  className?: string
}

export const VerticalCard = ({
  data,
  dense,
  sx = {},
  className,
}: IVerticalCardProps) => {
  const { t } = useTranslation()
  const {
    name,
    coverImage,
    venueLogo,
    startDate,
    endDate,
    hasLocation,
    isAd,
    status,
    //TODO: refactor getFeatureDetail type function later
    //@ts-ignore
  } = getFeatureDetail(data) as IEventDetail | IPromotionDetail

  const durationText = useMemo(
    () => getDurationDate(startDate, endDate),
    [startDate, endDate]
  )

  const theme = useTheme()

  const {
    root,
    cardMedia,
    cardContent,
    cardContentStack,
    venueLogoContainer,
    venueLogo: venueLogoStyle,
    chipContainer,
    highlightChip,
    comingSoonChip,
    durationContainer,
    duration,
    cardTitle,
    cardInfoContainer,
    iconContainer,
    locationInfo,
  } = theme.components?.[`VerticalCard`]?.styleOverrides || {}

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  return (
    <Card
      sx={deepmerge(root, sx)}
      className={clsx(dense ? "MuiCardContent-dense" : "", className)}
    >
      <CardMedia
        sx={cardMedia}
        className="VerticalCard-cardMedia"
        image={coverImage}
      />

      <CardContent sx={cardContent} className="VerticalCard-cardContent">
        <Stack sx={cardContentStack} className="VerticalCard-cardContentStack">
          {venueLogo && (
            <Box
              sx={venueLogoContainer}
              className="VerticalCard-venueLogoContainer"
            >
              <CardMedia
                sx={venueLogoStyle}
                className="VerticalCard-venueLogo"
                component="img"
                image={venueLogo}
              />
            </Box>
          )}
          <Box sx={chipContainer} className="VerticalCard-chipContainer">
            {isAd && (
              <HighlightChip
                sx={highlightChip}
                className="VerticalCard-highlightChip"
              />
            )}
            {status === StatusByDateRange.Upcoming && !isAd && (
              <ComingSoonChip
                sx={comingSoonChip}
                className="VerticalCard-comingSoonChip"
              />
            )}
          </Box>
          <Box
            sx={durationContainer}
            className="VerticalCard-durationContainer"
          >
            <Typography
              sx={duration}
              className="VerticalCard-durationText"
              component="p"
              variant="subTitle"
            >
              {durationText}
            </Typography>
          </Box>
          <Typography
            sx={cardTitle}
            className="VerticalCard-cardTitleText"
            variant="subTitle"
            component="div"
            color="text.primary"
          >
            {t(name)}
          </Typography>
        </Stack>
        {hasLocation && (
          <Box
            sx={cardInfoContainer}
            className="VerticalCard-cardInfoContainer"
          >
            <Box
              sx={iconContainer}
              className="VerticalCard-iconContainer"
              color="text.secondary"
            >
              {locationIcon}
            </Box>
            <LocationInfo
              sx={locationInfo}
              className="VerticalCard-locationInfo"
              feature={data}
              variant="subTitle"
              color="text.secondary"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
